import {css} from '../theme/css'
import {theme} from '../theme/theme'
import {makeStyles} from '../theme/UNSTABLE_makeStyles'
import {Size} from '../types'
import {
  Breakpoint,
  GridContentAlignment,
  GridDirection,
  GridItemsAlignment,
  GridJustification,
  GridSize,
  GridSpacing,
  GridWrap,
} from './Grid.types'

const baseClasses = {
  root: css``,
  container: css`
    box-sizing: border-box;
    display: flex;
    flex-wrap: wrap;
    width: 100%;
  `,
  item: css`
    box-sizing: border-box;
    margin: 0;
  `,
  zeroMinWidth: css`
    min-width: 0;
  `,
}
const createSpacing = (value: number): string => {
  const themeSpacing = theme.spacing(value)
  const gap = themeSpacing / 2

  return `
    margin: -${gap}px;
    width: calc(100% + ${themeSpacing}px);

    & > .${baseClasses.item} {
      padding: ${gap}px;
    }
  `
}
const spacing: Record<`spacing_${GridSpacing}`, string> = {
  spacing_1: css`
    ${createSpacing(1)}
  `,
  spacing_2: css`
    ${createSpacing(2)}
  `,
  spacing_3: css`
    ${createSpacing(3)}
  `,
  spacing_4: css`
    ${createSpacing(4)}
  `,
  spacing_5: css`
    ${createSpacing(5)}
  `,
  spacing_6: css`
    ${createSpacing(6)}
  `,
  spacing_7: css`
    ${createSpacing(7)}
  `,
  spacing_8: css`
    ${createSpacing(8)}
  `,
  spacing_9: css`
    ${createSpacing(9)}
  `,
  spacing_10: css`
    ${createSpacing(10)}
  `,
}
const sizes: Record<`size_${Size}`, string> = {
  size_xs: css`
    ${createSpacing(0.5)}
  `,
  size_s: css`
    ${createSpacing(1)}
  `,
  size_m: css`
    ${createSpacing(2)}
  `,
  size_l: css`
    ${createSpacing(3)}
  `,
  size_xl: css`
    ${createSpacing(4)}
  `,
}

const createGrid = (breakpoint: Breakpoint, value: GridSize): string => {
  const {flexBasis, flexGrow, maxWidth} = (() => {
    if (value === true) {
      return {
        flexGrow: 1,
        maxWidth: '100%',
        flexBasis: 0,
      }
    }

    if (value === 'auto') {
      return {
        flexGrow: 0,
        maxWidth: 'none',
        flexBasis: 'auto',
      }
    }

    const width = `${Math.round((value / 12) * 10e7) / 10e5}%`

    return {
      flexGrow: 0,
      maxWidth: width,
      flexBasis: width,
    }
  })()
  const style = `
    flex-grow: ${flexGrow};
    max-width: ${maxWidth};
    flex-basis: ${flexBasis};
  `

  return breakpoint === 'xs' ? style : `${theme.breakpoints.up(breakpoint)} {${style}}`
}
const grid: Record<`grid_${Breakpoint}_${GridSize}`, string> = {
  grid_xs_true: css`
    ${createGrid('xs', true)}
  `,
  grid_xs_auto: css`
    ${createGrid('xs', 'auto')}
  `,
  grid_xs_1: css`
    ${createGrid('xs', 1)}
  `,
  grid_xs_2: css`
    ${createGrid('xs', 2)}
  `,
  grid_xs_3: css`
    ${createGrid('xs', 3)}
  `,
  grid_xs_4: css`
    ${createGrid('xs', 4)}
  `,
  grid_xs_5: css`
    ${createGrid('xs', 5)}
  `,
  grid_xs_6: css`
    ${createGrid('xs', 6)}
  `,
  grid_xs_7: css`
    ${createGrid('xs', 7)}
  `,
  grid_xs_8: css`
    ${createGrid('xs', 8)}
  `,
  grid_xs_9: css`
    ${createGrid('xs', 9)}
  `,
  grid_xs_10: css`
    ${createGrid('xs', 10)}
  `,
  grid_xs_11: css`
    ${createGrid('xs', 11)}
  `,
  grid_xs_12: css`
    ${createGrid('xs', 12)}
  `,
  //
  grid_sm_true: css`
    ${createGrid('sm', true)}
  `,
  grid_sm_auto: css`
    ${createGrid('sm', 'auto')}
  `,
  grid_sm_1: css`
    ${createGrid('sm', 1)}
  `,
  grid_sm_2: css`
    ${createGrid('sm', 2)}
  `,
  grid_sm_3: css`
    ${createGrid('sm', 3)}
  `,
  grid_sm_4: css`
    ${createGrid('sm', 4)}
  `,
  grid_sm_5: css`
    ${createGrid('sm', 5)}
  `,
  grid_sm_6: css`
    ${createGrid('sm', 6)}
  `,
  grid_sm_7: css`
    ${createGrid('sm', 7)}
  `,
  grid_sm_8: css`
    ${createGrid('sm', 8)}
  `,
  grid_sm_9: css`
    ${createGrid('sm', 9)}
  `,
  grid_sm_10: css`
    ${createGrid('sm', 10)}
  `,
  grid_sm_11: css`
    ${createGrid('sm', 11)}
  `,
  grid_sm_12: css`
    ${createGrid('sm', 12)}
  `,
  //
  grid_md_true: css`
    ${createGrid('md', true)}
  `,
  grid_md_auto: css`
    ${createGrid('md', 'auto')}
  `,
  grid_md_1: css`
    ${createGrid('md', 1)}
  `,
  grid_md_2: css`
    ${createGrid('md', 2)}
  `,
  grid_md_3: css`
    ${createGrid('md', 3)}
  `,
  grid_md_4: css`
    ${createGrid('md', 4)}
  `,
  grid_md_5: css`
    ${createGrid('md', 5)}
  `,
  grid_md_6: css`
    ${createGrid('md', 6)}
  `,
  grid_md_7: css`
    ${createGrid('md', 7)}
  `,
  grid_md_8: css`
    ${createGrid('md', 8)}
  `,
  grid_md_9: css`
    ${createGrid('md', 9)}
  `,
  grid_md_10: css`
    ${createGrid('md', 10)}
  `,
  grid_md_11: css`
    ${createGrid('md', 11)}
  `,
  grid_md_12: css`
    ${createGrid('md', 12)}
  `,
  //
  grid_lg_true: css`
    ${createGrid('lg', true)}
  `,
  grid_lg_auto: css`
    ${createGrid('lg', 'auto')}
  `,
  grid_lg_1: css`
    ${createGrid('lg', 1)}
  `,
  grid_lg_2: css`
    ${createGrid('lg', 2)}
  `,
  grid_lg_3: css`
    ${createGrid('lg', 3)}
  `,
  grid_lg_4: css`
    ${createGrid('lg', 4)}
  `,
  grid_lg_5: css`
    ${createGrid('lg', 5)}
  `,
  grid_lg_6: css`
    ${createGrid('lg', 6)}
  `,
  grid_lg_7: css`
    ${createGrid('lg', 7)}
  `,
  grid_lg_8: css`
    ${createGrid('lg', 8)}
  `,
  grid_lg_9: css`
    ${createGrid('lg', 9)}
  `,
  grid_lg_10: css`
    ${createGrid('lg', 10)}
  `,
  grid_lg_11: css`
    ${createGrid('lg', 11)}
  `,
  grid_lg_12: css`
    ${createGrid('lg', 12)}
  `,
  //
  grid_xl_true: css`
    ${createGrid('xl', true)}
  `,
  grid_xl_auto: css`
    ${createGrid('xl', 'auto')}
  `,
  grid_xl_1: css`
    ${createGrid('xl', 1)}
  `,
  grid_xl_2: css`
    ${createGrid('xl', 2)}
  `,
  grid_xl_3: css`
    ${createGrid('xl', 3)}
  `,
  grid_xl_4: css`
    ${createGrid('xl', 4)}
  `,
  grid_xl_5: css`
    ${createGrid('xl', 5)}
  `,
  grid_xl_6: css`
    ${createGrid('xl', 6)}
  `,
  grid_xl_7: css`
    ${createGrid('xl', 7)}
  `,
  grid_xl_8: css`
    ${createGrid('xl', 8)}
  `,
  grid_xl_9: css`
    ${createGrid('xl', 9)}
  `,
  grid_xl_10: css`
    ${createGrid('xl', 10)}
  `,
  grid_xl_11: css`
    ${createGrid('xl', 11)}
  `,
  grid_xl_12: css`
    ${createGrid('xl', 12)}
  `,
}

const direction: Record<`direction_${GridDirection}`, string> = {
  direction_column: css`
    flex-direction: column;
  `,
  'direction_column-reverse': css`
    flex-direction: column-reverse;
  `,
  'direction_row-reverse': css`
    flex-direction: row-reverse;
  `,
  direction_row: '',
}
const wrap: Record<`wrap_${GridWrap}`, string> = {
  wrap_nowrap: css`
    flex-wrap: nowrap;
  `,
  'wrap_wrap-reverse': css`
    flex-wrap: wrap-reverse;
  `,
  wrap_wrap: '',
}
const alignItems: Record<`alignItems_${GridItemsAlignment}`, string> = {
  alignItems_center: css`
    align-items: center;
  `,
  'alignItems_flex-start': css`
    align-items: flex-start;
  `,
  'alignItems_flex-end': css`
    align-items: flex-end;
  `,
  alignItems_baseline: css`
    align-items: baseline;
  `,
  alignItems_stretch: '',
}
const alignContent: Record<`alignContent_${GridContentAlignment}`, string> = {
  alignContent_center: css`
    align-content: center;
  `,
  'alignContent_flex-start': css`
    align-content: flex-start;
  `,
  'alignContent_flex-end': css`
    align-content: flex-end;
  `,
  'alignContent_space-between': css`
    align-content: space-between;
  `,
  'alignContent_space-around': css`
    align-content: space-around;
  `,
  alignContent_stretch: '',
}
const justifyContent: Record<`justifyContent_${GridJustification}`, string> = {
  justifyContent_center: css`
    justify-content: center;
  `,
  'justifyContent_flex-end': css`
    justify-content: flex-end;
  `,
  'justifyContent_space-between': css`
    justify-content: space-between;
  `,
  'justifyContent_space-around': css`
    justify-content: space-around;
  `,
  'justifyContent_space-evenly': css`
    justify-content: space-evenly;
  `,
  'justifyContent_flex-start': '',
}

export const classes = {
  ...baseClasses,
  ...spacing,
  ...grid,
  ...sizes,
  ...direction,
  ...wrap,
  ...alignItems,
  ...alignContent,
  ...justifyContent,
}

export type ClassKey = keyof typeof classes
export const useClasses = makeStyles(classes)
